import React, { useEffect, useMemo, useState } from 'react'
import { Dropdown, Menu } from 'antd'
import { DotsThreeVertical } from '@phosphor-icons/react'
import CustomModal from 'components/global/CustomModal'
import Spinner from 'components/Spinner'
import { leagueGamesStore } from 'state/league/leageGames'
import { leagueEventStore } from 'state/league/leagueEvent'
import { useGetLeagueLogs } from 'api/league/leagueLog/leagueLog'
import { LeagueFoulResp, LeagueGoalResp, LeagueTimeoutResp } from 'types/league/leagueLog'
import EditLogModal, { EditableItem, TeamInfo } from './EditLogModal'
import moment from 'moment'
import '../styles/mainmodals/liveLog.scss'

type FilterType = 'all' | 'goals' | 'fouls' | 'timeouts'

type FeedItem =
  | { kind: 'goal';    data: LeagueGoalResp;    time: string; runningScore?: { home: number; away: number } }
  | { kind: 'foul';    data: LeagueFoulResp;    time: string; teamFouls?: number }
  | { kind: 'timeout'; data: LeagueTimeoutResp; time: string }

const KIND_MAP: Record<string, FeedItem['kind']> = {
  goals: 'goal', fouls: 'foul', timeouts: 'timeout',
}

const getPlayerName = (p: any): string => {
  if (!p) return ''
  if (typeof p === 'string') return p
  return `${p.firstName || ''} ${p.lastName || ''}`.trim()
}

const getTeamName = (leagueTeam: any): string => {
  if (!leagueTeam || typeof leagueTeam === 'string') return ''
  return leagueTeam.team?.name || leagueTeam.name || ''
}

const getInitials = (name: string): string => {
  const parts = name.trim().split(' ').filter(Boolean)
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
  return (name.slice(0, 2) || '?').toUpperCase()
}

// ── Score header ──────────────────────────────────────────────────────────────

type ScoreHeaderProps = {
  homeName: string
  awayName: string
  homeScore: number
  awayScore: number
}

const ScoreHeader: React.FC<ScoreHeaderProps> = ({ homeName, awayName, homeScore, awayScore }) => (
  <div className="ll-score-header">
    <div className="ll-score-team">
      <div className="ll-score-avatar ll-score-avatar--home">
        {homeName ? getInitials(homeName) : 'H'}
      </div>
      <span className="ll-score-team-name">{homeName || 'Home'}</span>
    </div>
    <div className="ll-score-middle">
      <div className="ll-score-display">
        <span className="ll-score-num">{homeScore}</span>
        <span className="ll-score-sep">–</span>
        <span className="ll-score-num">{awayScore}</span>
      </div>
      <span className="ll-score-status">Ongoing</span>
    </div>
    <div className="ll-score-team ll-score-team--away">
      <div className="ll-score-avatar ll-score-avatar--away">
        {awayName ? getInitials(awayName) : 'A'}
      </div>
      <span className="ll-score-team-name">{awayName || 'Away'}</span>
    </div>
  </div>
)

// ── Event icon ────────────────────────────────────────────────────────────────

const EventIcon: React.FC<{ kind: FeedItem['kind'] }> = ({ kind }) => (
  <div className={`ll-event-icon ll-event-icon--${kind}`}>
    {kind === 'goal'    && '⚽'}
    {kind === 'foul'    && '🟥'}
    {kind === 'timeout' && '⏱'}
  </div>
)

// ── Title builders ────────────────────────────────────────────────────────────

const goalTitle = (data: LeagueGoalResp): string => {
  const player = getPlayerName(data.player ?? (data as any).playerId)
  const team   = getTeamName(data.leagueTeam)
  if (player && team) return `Goal by ${player} (${team})`
  if (player) return `Goal by ${player}`
  if (team)   return `Goal by ${team}`
  return 'Goal'
}

const foulTitle = (data: LeagueFoulResp): string => {
  const team    = getTeamName(data.leagueTeam)
  const by      = data.foulPlayers?.map(getPlayerName).filter(Boolean).join(', ')
  const against = data.againstPlayers?.map(getPlayerName).filter(Boolean).join(', ')
  if (by && against && team) return `Foul by ${by} (${team}) vs ${against}`
  if (by && team)  return `Foul by ${by} (${team})`
  if (team)        return `Foul by ${team}`
  return 'Foul'
}

const timeoutTitle = (data: LeagueTimeoutResp): string => {
  const team = getTeamName(data.leagueTeam)
  return team ? `Timeout: ${team}` : 'Timeout'
}

// ── Feed card ─────────────────────────────────────────────────────────────────

const FeedCard: React.FC<{ item: FeedItem; onEdit: (item: EditableItem) => void }> = ({ item, onEdit }) => {
  const title =
    item.kind === 'goal'    ? goalTitle(item.data) :
    item.kind === 'foul'    ? foulTitle(item.data) :
    timeoutTitle(item.data)

  const statsLine = (() => {
    if (item.kind === 'goal' && item.runningScore) {
      const { home, away } = item.runningScore
      return `Score: ${home} – ${away}`
    }
    if (item.kind === 'foul' && item.teamFouls !== undefined) {
      return `Team Fouls: ${item.teamFouls}`
    }
    if (item.kind === 'timeout') {
      const d = (item.data as LeagueTimeoutResp).duration
      if (d) return `Duration: ${d}s`
    }
    return null
  })()

  const menu = (
    <Menu
      items={[
        {
          key: 'edit',
          label: 'Edit',
          onClick: () => onEdit({ kind: item.kind, data: item.data } as EditableItem),
        },
      ]}
    />
  )

  return (
    <div className={`ll-item ll-item--${item.kind}`}>
      <EventIcon kind={item.kind} />
      <div className="ll-item-content">
        <span className="ll-item-title">{title}</span>
        <span className="ll-item-time">{moment(item.time).format('M/D/YYYY h:mm A')}</span>
        {statsLine && <span className="ll-item-stats">{statsLine}</span>}
      </div>
      <Dropdown overlay={menu} trigger={['click']} placement="bottomRight">
        <button className="ll-item-action" title="More options">
          <DotsThreeVertical size={18} weight="bold" />
        </button>
      </Dropdown>
    </div>
  )
}

// ── Main component ────────────────────────────────────────────────────────────

const LiveLog: React.FC = () => {
  const [filter, setFilter]       = useState<FilterType>('all')
  const [editItem, setEditItem]   = useState<EditableItem | null>(null)

  const { liveLogModal, setLiveLogModal, selectedGame } = leagueGamesStore(store => ({
    liveLogModal:    store.liveLogModal,
    setLiveLogModal: store.setLiveLogModal,
    selectedGame:    store.selectedGame,
  }))

  const { leagueData } = leagueEventStore(store => ({ leagueData: store.leagueData }))
  const leagueId: string = (leagueData as any)?._id || ''

  const { data: logs, isLoading, isFetching, refetch } = useGetLeagueLogs(leagueId)

  useEffect(() => {
    if (leagueId) refetch()
  }, [leagueId])

  const loading = isLoading || isFetching

  // Home/away team info — prefer selectedGame, fall back to first log entry per side
  const homeTeamInfo = useMemo<TeamInfo>(() => {
    const g = selectedGame as any
    if (g?.homeTeam) return {
      name: g.homeTeam.team?.name || g.homeTeam.name || '',
      leagueTeamId: g.homeTeam._id || '',
      teamId: g.homeTeam.team?._id || '',
      photo: g.homeTeam.team?.profilePhoto?.thumb,
    }
    const all = [...(logs?.goals || []), ...(logs?.fouls || []), ...(logs?.timeouts || [])]
    const item = all.find((x: any) => x.teamKind === 'home') as any
    return {
      name: item ? getTeamName(item.leagueTeam) : '',
      leagueTeamId: item?.leagueTeamId || item?.leagueTeam?._id || '',
      teamId: item?.leagueTeam?.team?._id || '',
    }
  }, [selectedGame, logs])

  const awayTeamInfo = useMemo<TeamInfo>(() => {
    const g = selectedGame as any
    if (g?.awayTeam) return {
      name: g.awayTeam.team?.name || g.awayTeam.name || '',
      leagueTeamId: g.awayTeam._id || '',
      teamId: g.awayTeam.team?._id || '',
      photo: g.awayTeam.team?.profilePhoto?.thumb,
    }
    const all = [...(logs?.goals || []), ...(logs?.fouls || []), ...(logs?.timeouts || [])]
    const item = all.find((x: any) => x.teamKind === 'away') as any
    return {
      name: item ? getTeamName(item.leagueTeam) : '',
      leagueTeamId: item?.leagueTeamId || item?.leagueTeam?._id || '',
      teamId: item?.leagueTeam?.team?._id || '',
    }
  }, [selectedGame, logs])

  // Running score per goal (keyed by _id)
  const goalRunningScores = useMemo<Record<string, { home: number; away: number }>>(() => {
    const sorted = [...(logs?.goals || [])].sort(
      (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    )
    let h = 0, a = 0
    const map: Record<string, { home: number; away: number }> = {}
    for (const g of sorted) {
      if (g.teamKind === 'home') h++
      else a++
      map[g._id] = { home: h, away: a }
    }
    return map
  }, [logs?.goals])

  // Total fouls per leagueTeamId
  const teamFoulCounts = useMemo<Record<string, number>>(() => {
    const counts: Record<string, number> = {}
    for (const f of logs?.fouls || []) {
      counts[f.leagueTeamId] = (counts[f.leagueTeamId] || 0) + 1
    }
    return counts
  }, [logs?.fouls])

  const homeScore = (logs?.goals || []).filter(g => g.teamKind === 'home').length
  const awayScore = (logs?.goals || []).filter(g => g.teamKind === 'away').length

  const feed = useMemo<FeedItem[]>(() => {
    const goals: FeedItem[] = (logs?.goals || []).map(g => ({
      kind: 'goal',
      data: g,
      time: g.createdAt,
      runningScore: goalRunningScores[g._id],
    }))
    const fouls: FeedItem[] = (logs?.fouls || []).map(f => ({
      kind: 'foul',
      data: f,
      time: f.createdAt,
      teamFouls: teamFoulCounts[f.leagueTeamId],
    }))
    const timeouts: FeedItem[] = (logs?.timeouts || []).map(t => ({
      kind: 'timeout',
      data: t,
      time: t.createdAt,
    }))
    return [...goals, ...fouls, ...timeouts].sort(
      (a, b) => new Date(b.time).getTime() - new Date(a.time).getTime(),
    )
  }, [logs, goalRunningScores, teamFoulCounts])

  const counts = {
    goals:    logs?.goals?.length    || 0,
    fouls:    logs?.fouls?.length    || 0,
    timeouts: logs?.timeouts?.length || 0,
  }

  const filters: { key: FilterType; label: string; count: number }[] = [
    { key: 'all',      label: 'All events', count: counts.goals + counts.fouls + counts.timeouts },
    { key: 'goals',    label: 'Goals',      count: counts.goals },
    { key: 'fouls',    label: 'Fouls',      count: counts.fouls },
    { key: 'timeouts', label: 'Time-outs',  count: counts.timeouts },
  ]

  const filtered = filter === 'all' ? feed : feed.filter(item => item.kind === KIND_MAP[filter])

  return (
    <CustomModal
      openModal={liveLogModal}
      isCloseModalCustom
      closeModal={() => setLiveLogModal(false)}
      title="Live log"
      footer={false}
    >
      {loading ? (
        <div className="py-40px flex justify-center align-center">
          <Spinner />
        </div>
      ) : (
        <>
          <ScoreHeader
            homeName={homeTeamInfo.name}
            awayName={awayTeamInfo.name}
            homeScore={homeScore}
            awayScore={awayScore}
          />

          <div className="ll-filters">
            {filters.map(f => (
              <button
                key={f.key}
                className={`ll-filter-chip${filter === f.key ? ' ll-filter-chip--active' : ''}`}
                onClick={() => setFilter(f.key)}
              >
                {f.label}
                {f.count > 0 && <span className="ll-chip-count">{f.count}</span>}
              </button>
            ))}
          </div>

          {filtered.length === 0 ? (
            <div className="ll-empty">
              <p>No events logged yet</p>
              <span>Goals, fouls, and timeouts will appear here as they are recorded.</span>
            </div>
          ) : (
            <div className="ll-feed">
              {filtered.map((item, i) => (
                <FeedCard
                  key={i}
                  item={item}
                  onEdit={setEditItem}
                />
              ))}
            </div>
          )}
        </>
      )}

      {editItem && (
        <EditLogModal
          item={editItem}
          homeTeam={homeTeamInfo}
          awayTeam={awayTeamInfo}
          onClose={() => setEditItem(null)}
        />
      )}
    </CustomModal>
  )
}

export default LiveLog
